import { Draggable } from 'react-beautiful-dnd';
import { Card } from '@bancobarigui/barihaus';
import { Section, Group } from '../types';
import { SectionHeader } from './SectionHeader';
import { SectionContent } from './SectionContent';
import { useEditableTitle, useCollapse } from '../shared/hooks';

interface SectionItemProps {
  section: Section;
  groupId: string;
  index: number;
  onEdit: (sectionId: string, name: string) => void;
  onRemove: (sectionId: string) => void;
  onDuplicate: (sectionId: string) => void;
  onAddQuestion: (sectionId: string, insertIndex?: number) => void;
  onEditQuestion: (sectionId: string, questionId: string, updates: any) => void;
  onRemoveQuestion: (sectionId: string, questionId: string) => void;
  onDuplicateQuestion: (sectionId: string, questionId: string) => void;
  allGroups?: Group[];
  draggedType: string | null;
}

export function SectionItem({
  section,
  groupId,
  index,
  onEdit,
  onRemove,
  onDuplicate,
  onAddQuestion,
  onEditQuestion,
  onRemoveQuestion,
  onDuplicateQuestion,
  allGroups,
  draggedType,
}: SectionItemProps) {
  const { isEditing, editValue, setEditValue, startEditing, saveEdit, cancelEdit } =
    useEditableTitle({
      initialValue: section.name,
      onSave: (value) => onEdit(section.id, value),
    });

  const { isCollapsed, toggle: toggleCollapse } = useCollapse(false);

  return (
    <Draggable draggableId={`section-${section.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-3 ${snapshot.isDragging ? 'opacity-50' : ''}`}
        >
          <Card
            className="border border-gray-300 bg-gray-50"
            bodyStyle={{ padding: '12px' }}
          >
            <SectionHeader
              name={section.name}
              isEditing={isEditing}
              editValue={editValue}
              onEditValueChange={setEditValue}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onEdit={startEditing}
              onDuplicate={() => onDuplicate(section.id)}
              onRemove={() => onRemove(section.id)}
              isCollapsed={isCollapsed}
              onToggleCollapse={toggleCollapse}
              dragHandleProps={provided.dragHandleProps || null}
            />

            {!isCollapsed && (
              <SectionContent
                section={section}
                groupId={groupId}
                onAddQuestion={(insertIndex) => onAddQuestion(section.id, insertIndex)}
                onEditQuestion={(questionId, updates) =>
                  onEditQuestion(section.id, questionId, updates)
                }
                onRemoveQuestion={(questionId) => onRemoveQuestion(section.id, questionId)}
                onDuplicateQuestion={(questionId) => onDuplicateQuestion(section.id, questionId)}
                allGroups={allGroups || []}
                isDragging={snapshot.isDragging}
                draggedType={draggedType}
              />
            )}
          </Card>
        </div>
      )}
    </Draggable>
  );
}
